import { useState, useMemo, useCallback } from 'react';
import type { PolicyData } from './types';
import WidgetCard from './WidgetCard';
import ListWidget from './ListWidget';
import KpiCard from './KpiCard';
import UsMap from './UsMap';
import { useTheme } from '@/contexts/ThemeContext';

const STATE_NAME_TO_CODE: Record<string, string> = {
  Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',Colorado:'CO',
  Connecticut:'CT',Delaware:'DE','District of Columbia':'DC',Florida:'FL',Georgia:'GA',
  Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',Kentucky:'KY',
  Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',Michigan:'MI',Minnesota:'MN',
  Mississippi:'MS',Missouri:'MO',Montana:'MT',Nebraska:'NE',Nevada:'NV','New Hampshire':'NH',
  'New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',
  Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI',
  'South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',Utah:'UT',Vermont:'VT',
  Virginia:'VA',Washington:'WA','West Virginia':'WV',Wisconsin:'WI',Wyoming:'WY',
};

function toStateCode(s: string): string {
  if (!s) return s;
  if (s.length === 2) return s.toUpperCase();
  return STATE_NAME_TO_CODE[s] || s;
}

function getStatusClass(status: string): string {
  if (!status) return 'pi-status-other';
  const s = status.toLowerCase();
  if (s.includes('enacted') || s.includes('signed') || s.includes('became law')) return 'pi-status-enacted';
  if (s.includes('passed')) return 'pi-status-passed';
  if (s.includes('hearing')) return 'pi-status-hearing';
  if (s.includes('committee') || s.includes('referred') || s.includes('reading')) return 'pi-status-committee';
  if (s.includes('filed') || s.includes('introduced') || s.includes('prefiled')) return 'pi-status-filed';
  if (s.includes('unfavorab') || s.includes('failed') || s.includes('vetoed') || s.includes('dead')) return 'pi-status-unfavorable';
  return 'pi-status-other';
}

function getAuthorInitial(author: string): string {
  if (!author) return '?';
  const match = author.match(/(?:Rep\.|Sen\.|Del\.|Assemb\.|Gov\.)\s+(\w)/);
  if (match) return match[1];
  return author.charAt(0).toUpperCase();
}

const NEWS_ICONS: Record<string, string> = {
  'Resource Recycling': '♻️',
  'Plastics News': '🏭',
  'Packaging Dive': '📦',
  'Waste Dive': '🗑️',
  'Plastics Litigation': '⚖️',
  'APR': '🔄',
  'Upcoming Industry Events': '📅',
};

const NEWS_SUBTITLES: Record<string, string> = {
  'Resource Recycling': 'Recycling industry news and analysis',
  'Plastics News': 'Plastics industry coverage',
  'Packaging Dive': 'Packaging industry news',
  'Waste Dive': 'Waste & recycling news',
  'Plastics Litigation': 'Track plastics-related lawsuits',
  'APR': 'Association of Plastic Recyclers — Policy Hub',
  'Upcoming Industry Events': 'Conferences, trade shows, and events',
};

function renderSocialItem(p: PolicyData['socialMediaPosts'][0]) {
  return (
    <div className="pi-social-item" key={p.url || p.text.slice(0, 30)}>
      {p.imageUrl ? (
        <img className="pi-social-avatar" src={p.imageUrl} alt="" loading="lazy" />
      ) : (
        <div className="pi-social-avatar-placeholder">{getAuthorInitial(p.author)}</div>
      )}
      <div className="pi-social-content">
        <div className="pi-social-text">
          {p.url ? (
            <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              {p.text}
            </a>
          ) : p.text}
        </div>
        <div className="pi-social-meta">
          <span className="pi-platform-badge">{p.platform}</span>
          <span>{p.author?.split('|')[0]?.trim()}</span>
          <span>·</span>
          <span>{p.date}</span>
          {p.state && <span>· {p.state}</span>}
        </div>
      </div>
    </div>
  );
}

interface Props {
  data: PolicyData;
}

export default function Dashboard({ data }: Props) {
  const { isDark } = useTheme();
  const { bills, regulations, kpis, stateSummaries, industryEvents, socialMediaPosts } = data;
  const [stateFilter, setStateFilter] = useState<string | null>(null);

  const handleStateClick = useCallback((stateCode: string) => {
    setStateFilter(prev => prev === stateCode ? null : stateCode);
  }, []);

  const filteredBills = useMemo(() =>
    stateFilter ? bills.filter(b => b.state === stateFilter) : bills,
    [bills, stateFilter]
  );
  const filteredRegs = useMemo(() =>
    stateFilter ? regulations.filter(r => toStateCode(r.state) === stateFilter) : regulations,
    [regulations, stateFilter]
  );

  const newLaws = useMemo(() =>
    filteredBills.filter(b => ['Signed by the Governor', 'Enacted', 'Became Law'].some(s => b.status?.includes(s))),
    [filteredBills]
  );
  const upcomingHearings = useMemo(() =>
    filteredBills.filter(b => b.status?.includes('Hearing Scheduled')),
    [filteredBills]
  );
  const adoptedRegs = useMemo(() =>
    filteredRegs.filter(r => r.status?.includes('Adopted')),
    [filteredRegs]
  );
  const openForComments = useMemo(() =>
    filteredRegs.filter(r => r.status?.includes('Open for Comment') || r.status?.includes('Proposed')),
    [filteredRegs]
  );

  const billsByState = useMemo(() => {
    const map: Record<string, number> = {};
    stateSummaries?.forEach(s => { const code = toStateCode(s.state); if (s.billCount > 0) map[code] = (map[code] || 0) + s.billCount; });
    if (!Object.keys(map).length) {
      bills.forEach(b => { const code = toStateCode(b.state); if (code) map[code] = (map[code] || 0) + 1; });
    }
    return map;
  }, [bills, stateSummaries]);

  const regsByState = useMemo(() => {
    const map: Record<string, number> = {};
    stateSummaries?.forEach(s => { const code = toStateCode(s.state); if (s.regulationCount > 0) map[code] = (map[code] || 0) + s.regulationCount; });
    if (!Object.keys(map).length) {
      regulations.forEach(r => { const code = toStateCode(r.state); if (code) map[code] = (map[code] || 0) + 1; });
    }
    return map;
  }, [regulations, stateSummaries]);

  const statements = useMemo(() =>
    socialMediaPosts?.filter(p =>
      p.platform?.includes('Newsletter') || p.platform?.includes('Statement') || p.platform?.includes('Committee')
    ) || [],
    [socialMediaPosts]
  );
  const socialOnly = useMemo(() =>
    socialMediaPosts?.filter(p =>
      p.platform?.includes('Facebook') || p.platform?.includes('Tweet') || p.platform?.includes('Twitter')
    ) || [],
    [socialMediaPosts]
  );

  const newsSources = useMemo(() =>
    industryEvents?.filter(e =>
      ['Resource Recycling', 'Plastics News', 'Packaging Dive', 'Waste Dive', 'Plastics Litigation', 'APR'].includes(e.title)
    ) || [],
    [industryEvents]
  );
  const actualEvents = useMemo(() =>
    industryEvents?.filter(e =>
      !['Resource Recycling', 'Plastics News', 'Packaging Dive', 'Waste Dive', 'Plastics Litigation', 'APR'].includes(e.title)
    ) || [],
    [industryEvents]
  );

  const totalBills = kpis?.find(k => k.label.includes('Bills'))?.value || String(bills.length);
  const totalRegs = kpis?.find(k => k.label.includes('Regulations'))?.value || String(regulations.length);

  const renderBillItem = (b: typeof bills[0], i: number) => (
    <div className="pi-list-item" key={`${b.billNumber}${b.state}${i}`}>
      <a href={b.url} target="_blank" rel="noopener noreferrer">
        {b.billNumber}: {b.title}
      </a>
      <div className="meta">
        <span className={`pi-status-badge ${getStatusClass(b.status)}`}>{b.status}</span>
        {' '}· {b.state} · {b.sponsor || '—'} · {b.lastActionDate}
      </div>
    </div>
  );

  const renderRegItem = (r: typeof regulations[0], i: number) => (
    <div className="pi-list-item" key={`${r.title}${r.state}${i}`}>
      <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
      <div className="meta">
        <span className={`pi-status-badge ${getStatusClass(r.status)}`}>{r.status}</span>
        {' '}· {r.state} · {r.agency} · {r.lastActionDate}
      </div>
    </div>
  );

  return (
    <div className="policy-intel" data-theme={isDark ? undefined : 'light'}>
      <div className="pi-container">
        {/* Page header */}
        <div className="pt-5 pb-3 mb-4">
          <h1 className="text-3xl font-light" style={{ color: isDark ? '#e0e3de' : '#1e2019' }}>Policy & Regulatory Intelligence</h1>
          <p className="text-sm leading-relaxed mt-2" style={{ color: '#858d7e' }}>
            Tracking plastics legislation and regulatory activity across all 50 states.
            Turning complex policy shifts into clear, actionable intelligence for the resin industry.
          </p>
          <hr className="my-3 -mx-5 border-rs-jet" />
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="col-span-2 md:col-span-1"><KpiCard value={totalBills} label="Total Bills" /></div>
          <KpiCard value={totalRegs} label="Total Regulations" />
          <KpiCard value={String(upcomingHearings.length)} label="Upcoming Hearings" />
          {actualEvents?.length > 0 ? (
            <a href={actualEvents[0]?.url} target="_blank" rel="noopener noreferrer" className="kpi-card pi-kpi-events-link col-span-2 md:col-span-1">
              <div className="kpi-value">✦ Upcoming</div>
              <div className="kpi-label">Industry Events</div>
            </a>
          ) : (
            <KpiCard value={String(Object.keys(billsByState).length)} label="Active States" />
          )}
        </div>

        {/* State filter chip */}
        {stateFilter && (
          <div className="text-center">
            <div className="pi-state-filter-chip">
              Filtering by: {stateFilter}
              <button onClick={() => setStateFilter(null)}>✕</button>
            </div>
          </div>
        )}

        {/* Section: Legislative Tracking */}
        <div className="pi-section-banner mb-4">
          <h2>Legislative Tracking</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <ListWidget
            title="All Legislation"
            description="All plastics legislation filed for 2026 or carried over from 2025"
            count={`${filteredBills.length} Bills`}
            items={filteredBills}
            renderItem={renderBillItem}
            filterFn={(b, q) => `${b.billNumber} ${b.title} ${b.state} ${b.sponsor}`.toLowerCase().includes(q)}
            height={380}
          />
          <div className="order-first lg:order-none">
            <WidgetCard title="Where Bills Have Been Filed">
              <div className="p-4">
                <UsMap data={billsByState} secondaryData={regsByState} label="Bills" secondaryLabel="Regulations" onStateClick={handleStateClick} />
                <div className="text-center text-[0.625rem] mt-1" style={{ color: '#545b50' }}>
                  Click a state to filter all lists
                </div>
              </div>
            </WidgetCard>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <ListWidget
            title="Upcoming Hearings"
            description="All plastics legislation with hearings scheduled"
            count={`${upcomingHearings.length} Bills`}
            items={upcomingHearings}
            renderItem={renderBillItem}
            filterFn={(b, q) => `${b.billNumber} ${b.title} ${b.state}`.toLowerCase().includes(q)}
            height={380}
          />
          <ListWidget
            title="New Laws in the Last Year"
            description="All plastics laws enacted this year"
            count={`${newLaws.length} Bills`}
            items={newLaws}
            renderItem={renderBillItem}
            filterFn={(b, q) => `${b.billNumber} ${b.title} ${b.state}`.toLowerCase().includes(q)}
            height={380}
          />
        </div>

        {/* Section: Regulatory Tracking */}
        <div className="pi-section-banner mb-4">
          <h2>Regulatory Tracking</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <ListWidget
            title="All Regulations"
            description="All plastics regulations filed this year"
            count={`${filteredRegs.length} Regulations`}
            items={filteredRegs}
            renderItem={renderRegItem}
            filterFn={(r, q) => `${r.title} ${r.state} ${r.agency}`.toLowerCase().includes(q)}
            height={380}
          />
          <div className="order-first lg:order-none">
            <WidgetCard title="Where Regulatory Action is Occurring">
              <div className="p-4">
                <UsMap data={regsByState} secondaryData={billsByState} label="Regulations" secondaryLabel="Bills" onStateClick={handleStateClick} />
                <div className="text-center text-[0.625rem] mt-1" style={{ color: '#545b50' }}>
                  Click a state to filter all lists
                </div>
              </div>
            </WidgetCard>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <ListWidget
            title="Open for Comments"
            description="All plastics regulations currently accepting comments"
            count={`${openForComments.length} Regulations`}
            items={openForComments}
            renderItem={renderRegItem}
            filterFn={(r, q) => `${r.title} ${r.state}`.toLowerCase().includes(q)}
            height={380}
          />
          <ListWidget
            title="Regulations Adopted in the Last Year"
            description="All plastics regulations adopted this year"
            count={`${adoptedRegs.length} Regulations`}
            items={adoptedRegs}
            renderItem={renderRegItem}
            filterFn={(r, q) => `${r.title} ${r.state}`.toLowerCase().includes(q)}
            height={380}
          />
        </div>

        {/* Section: In the News & Industry Resources */}
        <div className="pi-section-banner mb-4">
          <h2>In the News &amp; Industry Resources</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {newsSources.map((ns, i) => (
            <a key={i} href={ns.url} target="_blank" rel="noopener noreferrer" className="pi-news-link-card">
              <span className="pi-news-icon">{NEWS_ICONS[ns.title] || '📰'}</span>
              <div>
                <div className="pi-news-title">{ns.title}</div>
                <div className="pi-news-subtitle">{NEWS_SUBTITLES[ns.title] || 'Industry news source'}</div>
              </div>
            </a>
          ))}
          {actualEvents.map((e, i) => (
            <a key={`evt-${i}`} href={e.url} target="_blank" rel="noopener noreferrer" className="pi-news-link-card">
              <span className="pi-news-icon">{NEWS_ICONS[e.title] || '📅'}</span>
              <div>
                <div className="pi-news-title">{e.title}</div>
                <div className="pi-news-subtitle">{NEWS_SUBTITLES[e.title] || 'Industry events and conferences'}</div>
              </div>
            </a>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <ListWidget
            title="Social Media Activity"
            description="Social media comments from public officials on plastics in the last three months"
            count={`${socialOnly.length} Posts`}
            items={socialOnly}
            renderItem={renderSocialItem}
            filterFn={(p, q) => `${p.text} ${p.author}`.toLowerCase().includes(q)}
            height={420}
          />
          <ListWidget
            title="Statements from Officials"
            description="Statements from public officials on plastics in the last three months"
            count={`${statements.length} Statements`}
            items={statements}
            renderItem={renderSocialItem}
            filterFn={(p, q) => `${p.text} ${p.author}`.toLowerCase().includes(q)}
            height={420}
          />
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-xs" style={{ color: '#545b50' }}>
          ResinSmart — An RTi Global Platform — Data updated {new Date(data.scrapedAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
